import axiosInstance from './axiosInstance';
import {
  Feature,
  FeatureDepartment,
  FeatureFilter,
  CreateFeatureForm,
  UpdateFeatureForm,
  FeatureListResponse,
  FeatureResponse,
  BusinessUnitViewResult,
  MultiDeptValue,
} from '../types/feature';

// ========== Feature API Services ==========

export const featureService = {
  // Get all features with filtering and pagination
  getFeatures: async (filters: FeatureFilter): Promise<FeatureListResponse> => {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.parentKey) params.append('parentKey', filters.parentKey);
    if (filters.businessUnit && filters.businessUnit !== 'ALL') params.append('businessUnit', filters.businessUnit);
    if (filters.dataScope && filters.dataScope !== 'ALL') params.append('dataScope', filters.dataScope);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await axiosInstance.get(`/features?${params.toString()}`);
    // The global axios response interceptor wraps "legacy" responses (those without
    // a top-level `success` key) as `{ success, message, data: <original body> }`.
    // Backend /features returns `{ docs, totalDocs, ... }` at top level, so after the
    // interceptor it lives at `response.data.data`. Unwrap to keep the public contract.
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body && 'data' in body && body.data && 'docs' in body.data) {
      return body.data as FeatureListResponse;
    }
    return body;
  },

  // Get feature by key
  getFeatureByKey: async (key: string): Promise<FeatureResponse> => {
    const response = await axiosInstance.get(`/features/${key}`);
    return response.data;
  },

  // Create new feature
  createFeature: async (data: CreateFeatureForm): Promise<FeatureResponse> => {
    const response = await axiosInstance.post('/features', data);
    return response.data;
  },

  // Update feature
  updateFeature: async (key: string, data: UpdateFeatureForm): Promise<FeatureResponse> => {
    const response = await axiosInstance.put(`/features/${key}`, data);
    return response.data;
  },

  // Delete feature
  deleteFeature: async (key: string): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(`/features/${key}`);
    return response.data;
  },

  // Toggle feature status
  toggleFeatureStatus: async (key: string): Promise<FeatureResponse> => {
    const response = await axiosInstance.patch(`/features/${key}/toggle-status`);
    return response.data;
  },

  // Get menu items for current user
  getMenuItems: async (): Promise<FeatureListResponse> => {
    const response = await axiosInstance.get('/features');
    return response.data;
  },

  // Initialize default features
  initializeFeatures: async (): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.post('/features/initialize');
    return response.data;
  },

  // Migrate existing features to add business unit assignments
  migrateDepartments: async (): Promise<{ success: boolean; message: string; data?: { updated: number; skipped: number } }> => {
    const response = await axiosInstance.post('/features/migrate-business-units');
    return response.data;
  },

  // Get all features (for dropdowns, etc.)
  getAllFeatures: async (): Promise<Feature[]> => {
    const response = await axiosInstance.get('/features');
    return response.data.docs || response.data;
  },

  // Get all features for a specific business unit (no pagination)
  getAllFeaturesForDept: async (businessUnit?: string): Promise<Feature[]> => {
    const params = businessUnit && businessUnit !== 'ALL' ? `?businessUnit=${businessUnit}` : '';
    const response = await axiosInstance.get(`/features/all-for-business-unit${params}`);
    return response.data.data || [];
  },

  // Get features grouped by business unit (business unit management overview)
  getDepartmentView: async (includeInactive = false): Promise<BusinessUnitViewResult> => {
    const response = await axiosInstance.get(`/features/business-unit-view?includeInactive=${includeInactive}`);
    return response.data.data;
  },

  // Bulk update the business unit for multiple features. Use businessUnits for "common to selected" (e.g. [OMS, AMS]).
  bulkUpdateDepartment: async (
    keys: string[],
    businessUnit: FeatureDepartment,
    businessUnits?: MultiDeptValue[]
  ): Promise<{ updated: number; notFound: string[] }> => {
    const body: { keys: string[]; businessUnit: FeatureDepartment; businessUnits?: MultiDeptValue[] } = { keys, businessUnit };
    if (businessUnits !== undefined && businessUnits.length > 0) body.businessUnits = businessUnits;
    const response = await axiosInstance.patch('/features/bulk-business-unit', body);
    return response.data.data;
  },

  // Update a feature by key
  updateFeatureByKey: async (key: string, data: Partial<CreateFeatureForm>): Promise<Feature> => {
    const response = await axiosInstance.put(`/features/${key}`, data);
    return response.data.data || response.data;
  },
}; 